import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('outplay_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Public/auth pages where a 401 should NOT trigger the global session-expired flow
const PUBLIC_PATHS = ['/', '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
const PUBLIC_PATH_PREFIXES = ['/arenax/auth', '/arenax/venues', '/arenax/squads', '/arenax/join-squad', '/arenax/games', '/arenax/matchmaking', '/academy/', '/feedback/', '/intake/onboard/'];
const ARENAX_PUBLIC_EXACT = ['/arenax'];

// Auth-related endpoints — a 401 here means "wrong credentials" or "expired reset token",
// NOT "your session expired". Suppress the global redirect for these.
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/validate-reset-token',
  '/auth/verify-email',
  '/arenax/auth/login',
  '/arenax/auth/register',
  '/arenax/auth/verify-email',
  '/arenax/auth/google-callback',
];

// De-dup the toast so a flurry of parallel failed requests doesn't stack
let _sessionExpiredHandled = false;

function _isAuthEndpoint(url) {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((p) => url.includes(p));
}

// Handle 401 responses globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const reqUrl = error.config?.url || '';

    if (status === 401 && !_isAuthEndpoint(reqUrl)) {
      // Phase 2L PR 5 — Spectator-room join is silently tried for non-auth viewers.
      // The component handles its own UX; skip the global toast/redirect.
      if (reqUrl && reqUrl.includes('/matches/') && reqUrl.includes('/spectate')) {
        return Promise.reject(error);
      }
      // Phase 296.d — QR-scan endpoint uses a *purpose-scoped* 30-second JWT
      // that's separate from the user's auth bearer. A 401 here means the QR
      // is stale/invalid, NOT that the parent's session expired. Let the
      // /scan page's own catch render scan-error-state with a 'Try again' CTA.
      if (reqUrl && reqUrl.includes('/attendance/qr-scan')) {
        return Promise.reject(error);
      }
      const detail = error.response?.data?.detail || '';
      const isExpired = detail.toLowerCase().includes('expired') || detail.toLowerCase().includes('session');

      const path = window.location.pathname;
      const onPublic = PUBLIC_PATHS.includes(path) || ARENAX_PUBLIC_EXACT.includes(path) || PUBLIC_PATH_PREFIXES.some(pref => path.startsWith(pref));
      // Phase 106 — magic-login also a public-friendly path: don't fire the
      // "Please log in to continue" toast over the dedicated error UI it renders.
      const isMagicLogin = path === '/magic-login' || path.startsWith('/magic-login?') || path.includes('/auth/magic-login');

      // Clear auth state regardless
      localStorage.removeItem('outplay_token');
      localStorage.removeItem('outplay_user');

      if (!onPublic && !isMagicLogin && !_sessionExpiredHandled) {
        _sessionExpiredHandled = true;
        // Friendly toast (de-duped)
        try {
          toast.error(isExpired ? 'Session expired. Please log in again.' : 'Please log in to continue.', { duration: 4000 });
        } catch (e) { /* toast unavailable */ }
        // Notify AuthContext so it clears `user` → ProtectedRoute will redirect
        // to /login?next={current_path} via React Router (no full page reload).
        try {
          window.dispatchEvent(new CustomEvent('outplay:auth-expired', { detail: { detail, path } }));
        } catch (e) { /* event API unavailable */ }
        // Reset flag after a short window
        setTimeout(() => { _sessionExpiredHandled = false; }, 1500);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE, BACKEND_URL };
